import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, finalize, map, of, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStore } from '../store/auth.store';
import { StorageService } from './storage.service';
import { AuthSession, RefreshResponse } from '../interfaces';

/** Margen previo al vencimiento en el que conviene renovar para no provocar un 401. */
const REFRESH_SKEW_MS = 2 * 60 * 1000;

/** Vigencia asumida si la respuesta no trae un `expiresIn` utilizable. */
const FALLBACK_EXPIRES_IN_SECONDS = 3600;

/** Error emitido cuando no hay refresh token con el que renovar. */
export const NO_REFRESH_TOKEN = 'NO_REFRESH_TOKEN';

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private http      = inject(HttpClient);
  private authStore = inject(AuthStore);
  private storage   = inject(StorageService);

  /**
   * Renovación en curso. Varias peticiones que vencen a la vez comparten una
   * sola llamada a /auth/refresh en lugar de dispararla en cascada.
   */
  private inFlight: Observable<string> | null = null;

  /**
   * Devuelve un token utilizable, renovándolo de forma anticipada cuando está a
   * punto de vencer. Si la renovación falla se entrega el token actual y será
   * el 401 del backend quien active el reintento.
   */
  getValidToken(): Observable<string | null> {
    const token = this.authStore.idToken();
    if (!token) return of(null);

    const expiresAt = this.authStore.expiresAt();
    const aboutToExpire = expiresAt !== null && expiresAt - Date.now() <= REFRESH_SKEW_MS;
    if (!aboutToExpire) return of(token);

    return this.refresh().pipe(catchError(() => of(token)));
  }

  refresh(): Observable<string> {
    if (this.inFlight) return this.inFlight;

    const session = this.storage.getSession();
    if (!session?.refreshToken) {
      return throwError(() => new Error(NO_REFRESH_TOKEN));
    }

    this.inFlight = this.http
      .post<RefreshResponse>(`${environment.apiUrl}/auth/refresh`, {
        refreshToken: session.refreshToken,
      })
      .pipe(
        map((response) => this.persist(session, response)),
        finalize(() => { this.inFlight = null; }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.inFlight;
  }

  private persist(previous: AuthSession, response: RefreshResponse): string {
    const seconds = Number(response.expiresIn);
    const vigencia = Number.isFinite(seconds) && seconds > 0 ? seconds : FALLBACK_EXPIRES_IN_SECONDS;

    const renewed: AuthSession = {
      ...previous,
      idToken: response.idToken,
      refreshToken: response.refreshToken || previous.refreshToken,
      expiresAt: Date.now() + vigencia * 1000,
    };

    this.storage.saveSession(renewed);
    this.authStore.updateTokens(renewed.idToken, renewed.refreshToken, renewed.expiresAt);
    return renewed.idToken;
  }
}
